import React, { useMemo, useOptimistic, useState } from 'react';
import { toast } from 'sonner';

import { ConfirmationDialog } from '@/components/ConfirmationDialog';
import { ToggleSwitch } from '@/components/legacy-ui';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import { useMy } from '@/hooks';
import { useUpdateFlags } from '@/hooks/useFlags';
import { SettingActionCard } from '@/sections/settings/components';
import { dangerButtonStyle } from '@/sections/settings/constants';
import { useAuth } from '@/state/auth';
import { ChariotModuleFlags, FlagType } from '@/types';

// Module-level noop to avoid creating new function references on each render
const noop = () => {};

const chariotModuleOptions: {
  name: string;
  description: string;
  value: FlagType;
  positiveFlag?: boolean;
  /** Flag value this option depends on being enabled */
  dependsOn?: FlagType;
  /** Human-readable name of the dependency for the hint message */
  dependencyLabel?: string;
  /** If true, the dependency uses positive flag logic (present = enabled) */
  dependsOnPositiveFlag?: boolean;
  /** If set, shows a confirmation dialog when enabling this feature */
  confirmationMessage?: string;
  /** If true, the toggle is permanently disabled with a "Coming Soon" label */
  comingSoon?: boolean;
}[] = [
  {
    name: 'Static IP Addresses',
    description: 'Enables scanning from a static IP pool',
    value: FlagType.DISABLE_STATIC_IP_ADDRESSES,
  },
  {
    name: 'AI Inference',
    description: "Enables AI Assistant's query mode",
    value: FlagType.DISABLE_AI_INFERENCE,
    confirmationMessage:
      'Enabling AI Inference allows the AI Assistant to process and respond to queries using your account data.',
  },
  {
    name: 'AI Agents',
    description: "Enables AI Assistant's agent mode",
    value: FlagType.ENABLE_AI_AGENTS,
    positiveFlag: true,
    dependsOn: FlagType.DISABLE_AI_INFERENCE,
    dependencyLabel: 'AI Inference',
    confirmationMessage:
      'Enabling AI Agents allows the AI Assistant to autonomously perform actions on your behalf, such as triaging vulnerabilities, validating seeds, and even targeting assets for compromise.',
  },
  {
    name: 'AI Training',
    description: 'Enables AI model training on account data',
    value: FlagType.DISABLE_AIMI_TRAINING,
    dependsOn: FlagType.ENABLE_AI_AGENTS,
    dependencyLabel: 'AI Agents',
    dependsOnPositiveFlag: true,
    confirmationMessage:
      'Enabling AI Training allows your account data to be used for improving AI model performance. Your data will be processed in accordance with our data handling policies.',
  },
  {
    name: 'AI Bring Your Own Model',
    description: 'Enables bringing your own AI model',
    value: FlagType.ENABLE_AI_BYOM,
    positiveFlag: true,
    comingSoon: true,
    confirmationMessage:
      'Enabling Bring Your Own Model allows you to configure a custom AI provider. You will be responsible for any costs incurred through your own provider.',
  },
];

/** Recursively collect all options that depend (directly or transitively) on a given flag */
const collectDependents = (
  flagValue: FlagType
): (typeof chariotModuleOptions)[number][] => {
  const directDependents = chariotModuleOptions.filter(
    opt => opt.dependsOn === flagValue
  );
  return directDependents.flatMap(dep => [
    dep,
    ...collectDependents(dep.value),
  ]);
};

interface PraetorianFeaturesSectionProps {
  email?: string;
  editable?: boolean;
  onDeleteAccount: () => void;
}

/**
 * Praetorian-only features section with feature flag toggles and account deletion.
 *
 * Features:
 * - Feature flag toggles (Static IP, AI Training)
 * - useOptimistic for instant toggle feedback with automatic rollback
 * - Delete account button (danger action)
 * - Only visible to Praetorian users and admin@praetorian.com
 * - Impersonation support via email prop
 *
 * Feature Flags:
 * - Static IP Addresses: Controls static IP pool for scanning
 * - AI Training: Controls AI model training on account data
 *
 * Special Handling:
 * - Static IP flag toggles both DISABLE_STATIC_IP_ADDRESSES and 'static' flags
 * - Flags are stored as disabled flags (inverted logic: flag present = feature disabled)
 *
 * React 19 Patterns:
 * - useOptimistic: Instant UI updates with automatic rollback on error
 * - Manual memoization for server flags (simple array filter, but needed for useOptimistic)
 *
 * Used in:
 * - OrganizationSettingsCards (conditional rendering for Praetorian users)
 */
export const PraetorianFeaturesSection: React.FC<
  PraetorianFeaturesSectionProps
> = ({ email, editable = true, onDeleteAccount }) => {
  const { me, friend } = useAuth();
  const currentEmail = email || friend || me;

  // Feature Flags (Praetorian only)
  const { data: flagsData, isLoading: isLoadingFlags } = useMy(
    {
      resource: 'flag',
    },
    {
      email: currentEmail,
      staleTime: 30000,
      gcTime: 300000,
    }
  );

  const { mutateAsync: updateFlags } = useUpdateFlags({
    email: currentEmail,
  });

  // Server state: Extract ChariotModuleFlags from API response
  // Memoization needed for useOptimistic dependency (not for performance)
  const serverFlags = useMemo(
    () =>
      (flagsData || [])
        .map(flag => flag.name)
        .filter(name => ChariotModuleFlags.includes(name as FlagType)),
    [flagsData]
  );

  // useOptimistic: Instant UI updates with automatic rollback on error
  // Second param is reducer that receives (currentState, optimisticValue)
  const [optimisticFlags, setOptimisticFlags] = useOptimistic(
    serverFlags,
    (_currentState, optimisticValue: string[]) => optimisticValue
  );

  const [updatingFlag, setUpdatingFlag] = useState<string | null>(null);

  // Confirmation dialog state for AI toggles
  const [pendingToggle, setPendingToggle] = useState<{
    checked: boolean;
    value: string;
    name: string;
    positiveFlag?: boolean;
    confirmationMessage: string;
  } | null>(null);

  // Transitive dependency check: walks the full dependency chain.
  // e.g., AI Training → AI Agents → AI Inference must ALL be enabled.
  const isDependencyChainMet = (
    option: (typeof chariotModuleOptions)[number]
  ): boolean => {
    if (!option.dependsOn) return true;

    // Check if direct dependency is enabled
    const depEnabled = option.dependsOnPositiveFlag
      ? optimisticFlags.includes(option.dependsOn)
      : !optimisticFlags.includes(option.dependsOn);

    if (!depEnabled) return false;

    // Recursively check the dependency's own dependencies
    const parentOption = chariotModuleOptions.find(
      o => o.value === option.dependsOn
    );
    if (parentOption) return isDependencyChainMet(parentOption);

    return true;
  };

  const handleToggleFlag = async (
    checked: boolean,
    value: string,
    name: string,
    positiveFlag?: boolean
  ) => {
    setUpdatingFlag(value);

    try {
      const newFlags = checked
        ? positiveFlag
          ? [...optimisticFlags, value]
          : optimisticFlags.filter(flag => flag !== value)
        : positiveFlag
          ? optimisticFlags.filter(flag => flag !== value)
          : [...optimisticFlags, value];

      // When disabling, cascade-disable all dependent features so they
      // don't silently re-enable when the prerequisite is turned back on
      const dependentsToDisable = !checked
        ? collectDependents(value as FlagType)
        : [];
      for (const dep of dependentsToDisable) {
        if (dep.positiveFlag) {
          const idx = newFlags.indexOf(dep.value);
          if (idx !== -1) newFlags.splice(idx, 1);
        } else {
          if (!newFlags.includes(dep.value)) newFlags.push(dep.value);
        }
      }

      // Optimistic update - useOptimistic will auto-rollback on error
      setOptimisticFlags(newFlags);

      // Normal flags: enabling = delete flag, disabling = add flag (inverted logic)
      // Positive flags: enabling = add flag, disabling = delete flag
      const flagsToAdd = positiveFlag
        ? checked
          ? [value]
          : []
        : !checked
          ? [value]
          : [];
      const flagsToDelete = positiveFlag
        ? checked
          ? []
          : [value]
        : checked
          ? [value]
          : [];

      // Static IP special handling: Toggle both DISABLE_STATIC_IP_ADDRESSES and 'static'
      if (value === FlagType.DISABLE_STATIC_IP_ADDRESSES) {
        if (checked) {
          flagsToAdd.push('static');
        } else {
          flagsToDelete.push('static');
        }
      }

      // Cascade: persist dependent flag changes to the API
      for (const dep of dependentsToDisable) {
        if (dep.positiveFlag) {
          if (
            optimisticFlags.includes(dep.value) &&
            !flagsToDelete.includes(dep.value)
          ) {
            flagsToDelete.push(dep.value);
          }
        } else {
          if (
            !optimisticFlags.includes(dep.value) &&
            !flagsToAdd.includes(dep.value)
          ) {
            flagsToAdd.push(dep.value);
          }
        }
      }

      await updateFlags({
        flagsToAdd,
        flagsToDelete,
        silent: true,
      });

      toast.success(`${name} has been ${checked ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error(`Failed to update ${name}. Please try again.`);
      console.error('Failed to update flag:', error);
    } finally {
      setUpdatingFlag(null);
    }
  };

  return (
    <>
      {/* Feature Toggle Cards */}
      {chariotModuleOptions.map(
        ({
          name,
          description,
          value,
          positiveFlag,
          comingSoon,
          dependsOn,
          dependencyLabel,
          dependsOnPositiveFlag,
        }) => {
          const isUpdating = updatingFlag === value;
          // Normal flags: flag present = disabled (inverted logic)
          // Positive flags: flag present = enabled (positive logic)
          const isEnabled = positiveFlag
            ? optimisticFlags.includes(value)
            : !optimisticFlags.includes(value);

          // Find the full option object to check transitive dependencies
          const currentOption = chariotModuleOptions.find(
            o => o.value === value
          );
          const isDependencyMet = currentOption
            ? isDependencyChainMet(currentOption)
            : true;

          // Find which dependency in the chain is actually unmet (for tooltip text)
          const getUnmetDependencyLabel = (): string | undefined => {
            if (isDependencyMet) return undefined;
            // Check direct dependency first
            if (dependsOn) {
              const directMet = dependsOnPositiveFlag
                ? optimisticFlags.includes(dependsOn)
                : !optimisticFlags.includes(dependsOn);
              if (!directMet) return dependencyLabel;
              // Direct is met but transitive isn't — find the ancestor that's unmet
              const parentOption = chariotModuleOptions.find(
                o => o.value === dependsOn
              );
              if (parentOption?.dependencyLabel) {
                return parentOption.dependencyLabel;
              }
            }
            return dependencyLabel;
          };
          const unmetLabel = getUnmetDependencyLabel();

          return (
            <SettingActionCard
              key={value}
              data-testid={`feature-${value}-card`}
              action={
                comingSoon ? (
                  <div className="flex items-center gap-2">
                    <span className="text-chariot-text-secondary text-xs font-medium">
                      Coming Soon
                    </span>
                    <ToggleSwitch
                      id={`feature-${value}`}
                      checked={false}
                      onChange={noop}
                      disabled
                    />
                  </div>
                ) : !isDependencyMet && unmetLabel ? (
                  <Tooltip title={`Enable ${unmetLabel} first`}>
                    {/* span wrapper enables hover/focus events on disabled button for tooltip */}
                    <span tabIndex={0}>
                      <ToggleSwitch
                        id={`feature-${value}`}
                        checked={isEnabled}
                        onChange={noop}
                        disabled
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <ToggleSwitch
                    id={`feature-${value}`}
                    checked={isEnabled}
                    onChange={checked => {
                      // Find the option to check for confirmationMessage
                      const option = chariotModuleOptions.find(
                        o => o.value === value
                      );
                      if (checked && option?.confirmationMessage) {
                        // Show confirmation dialog before enabling
                        setPendingToggle({
                          checked,
                          value,
                          name,
                          positiveFlag,
                          confirmationMessage: option.confirmationMessage,
                        });
                      } else {
                        // Disable actions proceed immediately (no confirmation needed)
                        handleToggleFlag(checked, value, name, positiveFlag);
                      }
                    }}
                    disabled={!editable || isLoadingFlags || isUpdating}
                  />
                )
              }
            >
              <div className="flex items-center gap-2">
                <h4 className="font-bold">{name}</h4>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-chariot-text-secondary text-xs">
                  {description}
                </span>
              </div>
            </SettingActionCard>
          );
        }
      )}

      {/* Account Deletion Card (Danger) */}
      <SettingActionCard
        data-testid="account-deletion-card"
        action={
          editable ? (
            <Button
              onClick={onDeleteAccount}
              className={dangerButtonStyle}
              size="sm"
              data-testid="delete-account-button"
            >
              Delete Account
            </Button>
          ) : undefined
        }
      >
        <div className="flex flex-col gap-2">
          <h4 className="font-bold text-red-600">Delete Account</h4>
          <span className="text-chariot-text-secondary text-xs">
            Permanently delete this account and all associated data
          </span>
        </div>
      </SettingActionCard>

      {/* Confirmation Dialog for AI Feature Enablement */}
      <ConfirmationDialog
        open={pendingToggle !== null}
        onClose={() => setPendingToggle(null)}
        onConfirm={() => {
          if (pendingToggle) {
            handleToggleFlag(
              pendingToggle.checked,
              pendingToggle.value,
              pendingToggle.name,
              pendingToggle.positiveFlag
            );
            setPendingToggle(null);
          }
        }}
        title={`Enable ${pendingToggle?.name ?? ''}?`}
        description={pendingToggle?.confirmationMessage ?? ''}
        confirmLabel="Enable"
        cancelLabel="Cancel"
        variant="default"
        data-testid="ai-feature-confirmation-dialog"
      />
    </>
  );
};
